import Vue from 'vue'
import {DateTime} from 'luxon'
import {Server_URL_ZK} from '@/shared/utils'

// Dienstplanung (Duty Planner) API client. The module id is allocated once in
// the central module register (ps.module) and is never customer-specific.
export const DP_MODULE_ID = 36

const base = () => Server_URL_ZK + '/zk/dp'

const get = (path, params) => Vue.http.get(base() + path, {params}).then(r => r.body)
const post = (path, body) => Vue.http.post(base() + path, body).then(r => r.body)
const enc = encodeURIComponent

export const dpApi = {
  settings: () => get('/settings'),
  feiertage: (von, bis) => get('/feiertage', {von, bis}),
  mitarbeiter: () => get('/mitarbeiter'),
  struktur: () => get('/struktur'),
  raster: () => get('/azra'),
  rasterDetail: bisId => get('/azra/' + enc(bisId)),
  rasterWochen: bisId => get('/azra/' + enc(bisId) + '/wochen'),
  gruppen: () => get('/gruppen'),
  gruppeSpeichern: g => post('/gruppen', g),
  modelle: () => get('/modelle'),
  modellSpeichern: m => post('/modelle', m),
  plan: (von, bis) => get('/plan', {von, bis}),
  entwurf: req => post('/entwurf', req),
  analyse: (von, bis) => get('/analyse', {von, bis}),
  vorschau: (von, bis) => post('/vorschau', {von, bis}),
  veroeffentlichen: vorschauLaufId => post('/veroeffentlichen', {vorschau_lauf_id: vorschauLaufId}),
  zuruecknehmen: (von, bis, basisRevision) => post('/zuruecknehmen', {von, bis, basis_revision: basisRevision}),
  laeufe: (von, bis) => get('/laeufe', {von, bis}),
  lauf: id => get('/laeufe/' + enc(id)),
  protokoll: vor => get('/protokoll', vor ? {vor} : {}),
}

// errorText turns an API error response into a German message.
export function errorText (response) {
  const body = (response && response.body) || {}
  if (Array.isArray(body.messages) && body.messages.length) {
    return body.messages.join(' ')
  }
  if (body.error) {
    return body.error
  }
  if (response && response.status === 0) {
    return 'Der Server ist nicht erreichbar.'
  }
  return 'Unbekannter Fehler (' + (response && response.status) + ').'
}

// isAuthError: the session expired (401). 403 means missing module/level and
// is shown as a message instead of redirecting.
export const isAuthError = response => response && response.status === 401

export const ERROR_CODES = {
  REGION_INVALID: 'Feiertagsregion des Mandanten ungültig',
  GAP: 'Keine Wochenquelle (ungeplante Lücke)',
  CREW_NOT_MAPPED: 'Kolonne ohne Musterwoche',
  NOT_ELIGIBLE: 'Nicht im Pool der Kolonne',
  DIRECT_NOT_MAPPED: 'Direkte Zuordnung ohne Musterwoche',
  MACHINE_NOT_MAPPED: 'Maschine ohne Musterwoche',
  DUTY_OVERLAP: 'Mehrere Maschinen am selben Tag',
  POSITION_UNKNOWN: 'Fahrerposition nicht vorhanden',
  POSITION_VACANT: 'Fahrerposition unbesetzt',
  COVER_INVALID: 'Vertretung ohne geplanten Dienst',
  PATTERN_MISSING: 'Standardraster fehlt',
  PATTERN_DAY_INVALID: 'Rastertag ungültig',
  EMPLOYEE_UNKNOWN: 'Mitarbeiter unbekannt',
  EMPLOYEE_BLOCKED: 'Mitarbeiter gesperrt',
  OUT_OF_SCOPE: 'Außerhalb des Planungsbereichs',
  DUPLICATE_TARGET_ROW: 'Doppelte Sollzeitzeile',
}

export const ACTIONS = {
  INSERT: {text: 'Neu', color: 'success'},
  UPDATE: {text: 'Geändert', color: 'primary'},
  UNCHANGED: {text: 'Unverändert', color: 'grey'},
  LOCKED: {text: 'Gesperrt (bestätigt)', color: 'warning'},
  ERROR: {text: 'Fehler', color: 'error'},
}

export const SOURCES = {
  TEMPORARY: 'Tagesvertretung',
  REGULAR: 'Dienstplan',
  STANDARD: 'Standardraster',
  DAY_OFF: 'Frei (keine Maschine)',
}

// weekly source of an employee (dp_wochenbesetzung.typ)
export const QUELLEN = {
  CREW: 'Kolonne',
  MACHINE: 'Maschinendienst',
  DIRECT: 'Direkt',
  STANDARD: 'Standardraster',
}

export const ZIELE = {
  CREW: 'Kolonne',
  MACHINE: 'Maschine',
  DIRECT: 'Einzelner Mitarbeiter',
}

export const GRUPPENTYPEN = [
  {value: 'KOLONNE', text: 'Kolonne'},
  {value: 'MASCHINE', text: 'Maschine'},
  {value: 'TEAM', text: 'Team'},
]

export const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag']
export const WOCHENTAGE_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

// mitaName resolves an employee name from the decrypted session data (the
// server never sends names). Falls back to the personnel/bis number.
export function mitaName (bisId, fallback) {
  if (!bisId) {
    return ''
  }
  try {
    const res = JSON.parse(sessionStorage.getItem(bisId.includes('-') ? bisId : bisId + '-'))
    if (res && (res.first_name || res.surname)) {
      return ((res.surname || '') + ', ' + (res.first_name || '')).replace(/^, |, $/g, '')
    }
    if (res && res.full_name) {
      return res.full_name
    }
  } catch (e) {
    // ignore broken session entries
  }
  return fallback || ('Mitarbeiter ' + bisId)
}

export const fmtDay = iso => iso ? DateTime.fromISO(iso).toFormat('dd.MM.yyyy') : ''
export const fmtDayShort = iso => iso ? DateTime.fromISO(iso).toFormat('dd.MM.') : ''
export const fmtHours = h => (Number(h) || 0).toFixed(2).replace('.', ',')
export const fmtHHMM = h => {
  const min = Math.round((Number(h) || 0) * 60)
  return String(Math.floor(min / 60)).padStart(2, '0') + ':' + String(min % 60).padStart(2, '0')
}

export function daysBetween (von, bis) {
  const res = []
  let d = DateTime.fromISO(von)
  const end = DateTime.fromISO(bis)
  while (d <= end) {
    res.push(d)
    d = d.plus({days: 1})
  }
  return res
}

// ---------- ISO weeks (identical rules to the server) ----------

export const isoKey = dt => dt.weekYear + '-W' + String(dt.weekNumber).padStart(2, '0')

export function weekInfo (key) {
  const m = /^(\d{4})-W(\d{2})$/.exec(key || '')
  if (!m) return null
  const monday = DateTime.fromObject({weekYear: Number(m[1]), weekNumber: Number(m[2]), weekday: 1})
  if (!monday.isValid || isoKey(monday) !== key) return null
  return {
    key, montag: monday.toISODate(), sonntag: monday.plus({days: 6}).toISODate(), nummer: monday.weekNumber,
    paritaet: monday.weekNumber % 2 === 1 ? 'ODD' : 'EVEN',
  }
}

// periodWeeks lists the ISO weeks touching von..bis (max. 53).
export function periodWeeks (von, bis) {
  const f = DateTime.fromISO(von)
  const t = DateTime.fromISO(bis)
  if (!f.isValid || !t.isValid || f > t) return []
  const res = []
  for (let d = f.startOf('week'); d <= t; d = d.plus({weeks: 1})) {
    res.push(weekInfo(isoKey(d)))
    if (res.length > 53) return []
  }
  return res
}

export const weekLabel = w => w ? 'KW ' + w.nummer + ' · ' + fmtDayShort(w.montag) + '–' + fmtDayShort(w.sonntag) : ''
export const parityText = p => p === 'ODD' ? 'ungerade' : 'gerade'

// inPhase: all 7 days of the week lie in the phase von..bis (MM-DD, may wrap).
export function inPhase (montag, von, bis) {
  const m = DateTime.fromISO(montag)
  for (let i = 0; i < 7; i++) {
    const md = m.plus({days: i}).toFormat('MM-dd')
    const inside = von <= bis ? md >= von && md <= bis : md >= von || md <= bis
    if (!inside) return false
  }
  return true
}

export const phaseLabel = ph => ph ? ph.von.split('-').reverse().join('.') + ' – ' + ph.bis.split('-').reverse().join('.') : ''

export const weekHours = tage => (tage || []).reduce((s, d) => s + (Number(d.zeit_soll) || 0), 0)

// default planning range: current ISO week plus the next four
export function defaultRange () {
  const monday = DateTime.local().startOf('week')
  return {von: monday.toISODate(), bis: monday.plus({weeks: 4, days: 6}).toISODate()}
}
